import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useChat } from "@/context/ChatContext";

export default function FinalCTA() {
  const { openChat, waDefault } = useChat();
  const [form, setForm] = useState({ name: "", whatsapp: "", category: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data } = await api.post("/contact", {
        name: form.name,
        whatsapp: form.whatsapp,
        message: form.message,
        category: form.category || null,
      });
      toast.success(data.message || "Recebido! A Isabela vai te chamar em breve.");
      setForm({ name: "", whatsapp: "", category: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível enviar. Confira os dados e tente de novo."));
    } finally {
      setSending(false);
    }
  }

  return (
    <section id="fale" className="scroll-mt-24 bg-white py-24 md:py-32" data-testid="final-cta-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-[2.5rem] bg-brand-800 px-6 py-14 sm:px-12 lg:px-16"
        >
          <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-700/60 blur-3xl" />
          <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-brand-900/70 blur-3xl" />

          <div className="relative grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-200">Pronto para começar?</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Sua conquista começa com uma conversa
              </h2>
              <p className="mt-4 max-w-md text-base leading-relaxed text-brand-100/90">
                Monte seu perfil em poucas perguntas e receba um plano de consórcio sob medida —
                imóvel ou veículo, sem juros, com planejamento.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button
                  data-testid="final-chat-button"
                  onClick={() => openChat(null)}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-bold text-brand-700 transition-all hover:-translate-y-0.5 hover:shadow-xl"
                >
                  <MessageCircle className="h-4 w-4" /> Começar pelo chat
                </button>
                <a
                  href={waDefault()}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="final-whatsapp-button"
                  className="inline-flex items-center justify-center rounded-full border-2 border-white/30 px-7 py-4 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:border-white/60"
                >
                  Chamar no WhatsApp
                </a>
              </div>
              <p className="mt-6 max-w-md text-[11px] leading-relaxed text-brand-200/70">
                Simulações e condições sujeitas à análise e às regras do grupo de consórcio vigente.
                Consórcio tem taxa de administração e fundo de reserva.
              </p>
            </div>

            <div className="rounded-3xl bg-white p-7 shadow-2xl sm:p-8" data-testid="contact-form-card">
              <h3 className="font-display text-lg font-bold text-slate-900">Prefere sem chat? Deixe seu recado</h3>
              <p className="mt-1 text-base text-slate-500">A Isabela responde pessoalmente pelo WhatsApp.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label htmlFor="ct-name" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Nome</label>
                  <Input
                    id="ct-name"
                    data-testid="contact-name-input"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={(e) => set("name")(e.target.value)}
                    placeholder="Seu nome"
                  />
                </div>
                <div>
                  <label htmlFor="ct-wa" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">WhatsApp (com DDD)</label>
                  <Input
                    id="ct-wa"
                    data-testid="contact-whatsapp-input"
                    required
                    value={form.whatsapp}
                    onChange={(e) => set("whatsapp")(e.target.value)}
                    placeholder="(71) 90000-0000"
                    inputMode="tel"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Interesse</label>
                  <Select value={form.category} onValueChange={set("category")}>
                    <SelectTrigger data-testid="contact-category-select" className="w-full">
                      <SelectValue placeholder="Imóvel, veículo ou ainda não sei" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="imovel">Consórcio de imóvel</SelectItem>
                      <SelectItem value="veiculo">Consórcio de veículo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label htmlFor="ct-msg" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Mensagem</label>
                  <Textarea
                    id="ct-msg"
                    data-testid="contact-message-input"
                    required
                    rows={3}
                    value={form.message}
                    onChange={(e) => set("message")(e.target.value)}
                    placeholder="Me conta o que você quer conquistar..."
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-7 py-4 text-sm font-bold text-white transition-all hover:bg-brand-700 disabled:opacity-60"
                >
                  <Send className="h-4 w-4" /> {sending ? "Enviando..." : "Enviar recado"}
                </button>
                <p className="text-[11px] leading-relaxed text-slate-400">
                  Ao enviar, você consente o contato sobre consórcio (LGPD). Simulações sujeitas à
                  análise e às regras do grupo vigente.
                </p>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
